// Added by Sejune Oh on 2019-06-21
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, Fragment } from 'react'
import { createStyles, withStyles, WithStyles, Theme, Fade } from '@material-ui/core'
import { LoginFormBaseFCProps } from './LoginForm'
import Intl from '@/helper/Intl'
import classNames from 'classnames'
import {
    AccountCircleIcon,
    VisibilityIcon,
    VisibilityOffIcon,
    LockIcon,
    Button,
    TextField,
    InputAdornment,
    FormControlLabel,
    Checkbox,
    Snackbar,
    SnackbarContent,
    Typography,
} from '@/components/_primes'
import { SNACK_BAR_VALID_TIME } from '@/styles/Theme'
import LoginStyle from '@/styles/login/LoginStyle'

interface MLoginFormFCProps extends WithStyles<typeof styles>, LoginFormBaseFCProps { }

const MLoginForm: FC<MLoginFormFCProps> = (props) => {
    const { classes } = props
    const labelId = Intl.getIntlText({ id: 'htmljs_react.login.mobileloginform.label_id' })
    const labelNameOrEmail = Intl.getIntlText({ id: 'htmljs_react.login.mobileloginform.label_nameoremail' })
    const labelPassword = Intl.getIntlText({ id: 'htmljs_react.login.mobileloginform.label_password' })
    const labelKeepMeLoggedIn = Intl.getIntlText({ id: 'htmljs_react.login.mobileloginform.label_keepmeloggedin' })
    const btnLogIn = Intl.getIntlText({ id: 'htmljs_react.login.mobileloginform.btn_login' })
    const msgInvalidAccount = Intl.getIntlText({ id: 'htmljs_react.login.mobileloginform.msg_invalid_account' })

    return (
        <Fragment>
            <div className={classes.Wrapper}>
                <div className={classes.LoginWrappr}>
                    <div className={classes.LoginInputWrapper}>
                        <div className={classes.CPWrapper}>
                            {props.useLoginFormTopImage ? (
                                <div
                                    className={classes.CPLogoImgWrapper}
                                    style={{ backgroundImage: props.loginTopImage }}
                                />
                            ) : (
                                    <div className={classNames(classes.CPLogo, classes.CPLogoMain)}>{props.productBI}</div>
                                )}
                        </div>
                        <TextField
                            id="email"
                            className={classes.LoginFormInput}
                            label={labelId}
                            placeholder={labelNameOrEmail}
                            margin="normal"
                            variant="outlined"
                            type="text"
                            name="email"
                            onChange={props.onValueChange}
                            autoComplete="email nickname"
                            autoFocus={!props.savedEmail}
                            defaultValue={props.savedEmail}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccountCircleIcon />
                                    </InputAdornment>
                                ),
                                onKeyUp: props.onKeyUp,
                            }}
                            inputProps={{
                                className: classes.AutoFocusableInput,
                            }}
                        />
                        <TextField
                            id="password"
                            className={classes.LoginFormInput}
                            label={labelPassword}
                            margin="normal"
                            variant="outlined"
                            name="password"
                            onChange={props.onValueChange}
                            autoComplete="current-password"
                            autoFocus={!!props.savedEmail}
                            type={props.hidePassword ? 'password' : 'text'}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="start" onClick={props.onPasswordInputIconClick}>
                                        {props.hidePassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    </InputAdornment>
                                ),
                                onKeyUp: props.onKeyUp,
                            }}
                            inputProps={{
                                className: classes.AutoFocusableInput,
                            }}
                        />
                        {props.enableKeepSigninStatus && (
                            <FormControlLabel
                                className={classes.LoginFormCtrLabel}
                                onChange={props.onCheckedChange}
                                control={<Checkbox checked={props.isChecked} value="remember" color="primary" />}
                                label={labelKeepMeLoggedIn}
                            />
                        )}
                    </div>

                    <Button
                        id={props.loginBtnId}
                        btnText={btnLogIn}
                        className={classes.LoginBtn}
                        buttonType={'task'}
                        onClick={props.onBtnClick}
                        disabled={props.isOnLoading}
                    />
                    <Typography className={classes.LoginPaperCopyRight} align="center">
                        {props.copyRight}({props.version})
                    </Typography>
                </div>
            </div>
            {/** Snack bar for Login Status */}
            <Snackbar
                className={classes.LoginSnackbar}
                open={props.isErrorOccured}
                onClose={props.onSnackbarClose}
                TransitionComponent={Fade}
                autoHideDuration={SNACK_BAR_VALID_TIME}
            >
                <SnackbarContent
                    className={classes.LoginSnackbarContent}
                    evtType="error"
                    message={props.errMessage || msgInvalidAccount}
                    onClose={props.onSnackbarClose}
                />
            </Snackbar>
        </Fragment>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = (theme: Theme) =>
    createStyles({
        ...LoginStyle(),
        Wrapper: {
            position: 'absolute',
            top: '16%',
            left: 0,
            right: 0,
            margin: 'auto',
            display: 'flex',
            justifyContent: 'center',
        },
        LoginWrappr: {
            width: '90%',
            height: '400px',
            borderRadius: '4px',
            backgroundColor: '#292a2c',
            padding: `${theme.spacing() * 1.5}px`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative',
        },
        LoginInputWrapper: {
            display: 'flex',
            flexDirection: 'column',
        },
        LoginBtn: {
            height: '45px',
            fontSize: '18px',
        },
        CPWrapper: {
            display: 'flex',
            alignItems: 'baseline',
            marginBottom: '12px',
            flexDirection: 'column',
        },
        CPLogoMain: {
            fontSize: '2rem',
        },
        LoginSnackbar: {
            position: 'absolute',
            width: '100%',
        },
        CPLogoImgWrapper: {
            height: '46px',
            width: '100%',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'auto 100%',
        },
    })

export default withStyles(styles, { withTheme: true })(MLoginForm)
